/**
 * useCertLogin — native browser TLS client-certificate login hook.
 *
 * The flow is identical to how sede.agenciatributaria.gob.es works:
 *   1. A POST is made to port 8443 — the dedicated Nginx listener configured
 *      with ssl_verify_client so the cert picker fires during the TLS handshake.
 *      The URL is built at runtime from window.location so it works on any domain.
 *   2. The browser shows the OS native cert picker (TLS handshake)
 *   3. The user selects their FNMT certificate — private key never leaves the OS
 *   4. Nginx forwards X-SSL-Client-Verify + X-SSL-Client-DN headers to FastAPI
 *   5. FastAPI returns nif, full_name, access_token
 *   6. This hook stores the token via setAuthToken() for use by apiClient
 *
 * No AutoFirma. No JavaScript cert handling. No WebSocket. No file upload.
 */

import { useCallback, useState } from 'react';
import { post } from '../../../shared/apiClient';
import { setAuthToken } from '../../../shared/auth';
import { ENDPOINTS } from '../../../shared/endpoints';

export function useCertLogin() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [nif, setNif] = useState(null);
  const [fullName, setFullName] = useState(null);

  const login = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // ENDPOINTS.certAuth.login returns an absolute URL with port 8443, built
      // from window.location.origin at call time — no hardcoded domain.
      // Example: https://taxcalculator.disrupt.ltd:8443/api/v1/auth/cert-login
      //
      // apiClient.post() detects the https:// prefix and uses the URL as-is
      // (no baseUrl prepended). No body — cert is presented at the TLS layer.
      const { data } = await post(ENDPOINTS.certAuth.login);

      setAuthToken(data.access_token);
      setNif(data.nif);
      setFullName(data.full_name);
      setIsAuthenticated(true);

      return data;
    } catch (err) {
      let message;
      if (err && err.status === 401) {
        message =
          'No certificate found. Please ensure your FNMT certificate is installed ' +
          'in your browser or OS certificate store and try again.';
      } else if (err && err.status === 400) {
        message = 'The certificate you selected is not a valid FNMT citizen certificate.';
      } else if (err && err.message) {
        message = err.message;
      } else {
        message = 'Certificate login failed. Please try again.';
      }
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setAuthToken(null);
    setNif(null);
    setFullName(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  return { login, logout, isLoading, error, isAuthenticated, nif, fullName };
}
